import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Users, Search, AlertCircle, ChevronLeft, TrendingUp, TrendingDown } from 'lucide-react';
import DriverDetailModal from './DriverDetailModal';

const API_URL = process.env.REACT_APP_API_URL;

function AdminDrivers({ setActiveComponent }) {
  const [drivers, setDrivers] = useState([]);
  const [filteredDrivers, setFilteredDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [sortBy, setSortBy] = useState('name'); // 'name', 'success_rate', 'total_deliveries'
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc', 'desc'

  useEffect(() => {
    fetchDrivers();
  }, []);

  useEffect(() => {
    filterAndSortDrivers();
  }, [drivers, searchTerm, sortBy, sortOrder]);

  async function fetchDrivers() {
    try {
        setLoading(true);
        const response = await axios.get(`${API_URL}/api/admin/drivers/admin`);
        
        // The data is nested under 'drivers' key
        const driversData = response.data.drivers || response.data;
        
        // Validate response is an array
        if (Array.isArray(driversData)) {
        setDrivers(driversData);
        } else {
        console.error('Invalid response format:', response.data);
        setDrivers([]);
        alert(`Error: Formato de respuesta inválido. Recibido: ${JSON.stringify(response.data)}`);
        }
    } catch (error) {
        console.error('Error fetching drivers:', error);
        console.error('Error response:', error.response?.data);
        setDrivers([]);
        alert('Error al cargar conductores: ' + (error.response?.data?.error || error.message));
    } finally {
        setLoading(false);
    }
    }

  function filterAndSortDrivers() {
    let filtered = [...drivers];

    // Filter by search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(driver =>
        driver.name?.toLowerCase().includes(term) ||
        driver.email?.toLowerCase().includes(term) ||
        driver.phone?.includes(term)
      );
    }

    // Sort
    filtered.sort((a, b) => {
      let aVal, bVal;

      switch (sortBy) {
        case 'success_rate':
          aVal = a.success_rate;
          bVal = b.success_rate;
          break;
        case 'total_deliveries':
          aVal = a.total_deliveries;
          bVal = b.total_deliveries;
          break;
        case 'name':
        default:
          aVal = a.name?.toLowerCase() || '';
          bVal = b.name?.toLowerCase() || '';
          break;
      }

      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    setFilteredDrivers(filtered);
  }

  function handleSort(column) {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  }

  function getSuccessRateColor(rate) {
    if (rate >= 87) return 'text-[#E6B43C]'; // Gold - Extra bonus
    if (rate >= 83) return 'text-[#67A43C]'; // Green - Bonus eligible
    if (rate >= 75) return 'text-slate-600'; // Satisfactory
    return 'text-red-600'; // Underperforming
  }

  function getSuccessRateBg(rate) {
    if (rate >= 87) return 'bg-[#E6B43C]/10';
    if (rate >= 83) return 'bg-[#67A43C]/10';
    if (rate >= 75) return 'bg-slate-100';
    return 'bg-red-50';
  }

  function getPerformanceIcon(rate) {
    if (rate >= 75) {
      return <TrendingUp className="w-4 h-4" />;
    }
    return <TrendingDown className="w-4 h-4" />;
  }

  function openDriverModal(driver) {
    setSelectedDriver(driver);
    setShowModal(true);
  }

  function closeModal() {
    setShowModal(false);
    setSelectedDriver(null);
  }

  function handleDriverUpdated() {
    fetchDrivers(); // Refresh list
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#B1336E] mx-auto"></div>
              <p className="mt-4 text-slate-600 font-montserrat">Cargando conductores...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <button
              onClick={() => setActiveComponent('OptionsAdmin')}
              className="flex items-center gap-2 text-slate-700 hover:text-[#B1336E] transition-colors duration-200 font-montserrat"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="p-3 bg-[#B1336E] rounded-xl shadow-lg">
              <Users className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-slate-800 font-montserrat">
              Gestión de Conductores
            </h1>
          </div>
          <p className="text-slate-600 font-montserrat ml-16">
            Administra la información y métricas de tus conductores
          </p>
        </div>

        {/* Search Bar */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder="Buscar por nombre, email o teléfono..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-11 pr-4 py-3 border-2 border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B1336E]/20 focus:border-[#B1336E] transition-all font-montserrat"
            />
          </div>
        </div>

        {/* Stats Summary */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide font-montserrat mb-1">
                Total Conductores
                </p>
                <p className="text-3xl font-bold text-[#B1336E] font-montserrat">
                {Array.isArray(drivers) ? drivers.length : 0}
                </p>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide font-montserrat mb-1">
                Activos (7 días)
                </p>
                <p className="text-3xl font-bold text-[#67A43C] font-montserrat">
                {Array.isArray(drivers) ? drivers.filter(d => d.is_active).length : 0}
                </p>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide font-montserrat mb-1">
                Éxito Promedio
                </p>
                <p className="text-3xl font-bold text-slate-700 font-montserrat">
                {Array.isArray(drivers) && drivers.length > 0
                    ? Math.round(drivers.reduce((sum, d) => sum + d.success_rate, 0) / drivers.length)
                    : 0}%
                </p>
            </div>
        </div>

        {/* Drivers Table */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <button
                      onClick={() => handleSort('name')}
                      className="flex items-center gap-2 text-xs font-bold text-slate-700 uppercase tracking-wide font-montserrat hover:text-[#B1336E] transition-colors"
                    >
                      Conductor
                      {sortBy === 'name' && (
                        <span className="text-[#B1336E]">
                          {sortOrder === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 uppercase tracking-wide font-montserrat hidden md:table-cell">
                    Contacto
                  </th>
                  <th className="px-4 py-3 text-center">
                    <button
                      onClick={() => handleSort('success_rate')}
                      className="flex items-center gap-2 text-xs font-bold text-slate-700 uppercase tracking-wide font-montserrat hover:text-[#B1336E] transition-colors mx-auto"
                    >
                      Éxito (%)
                      {sortBy === 'success_rate' && (
                        <span className="text-[#B1336E]">
                          {sortOrder === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </button>
                  </th>
                  <th className="px-4 py-3 text-center">
                    <button
                      onClick={() => handleSort('total_deliveries')}
                      className="flex items-center gap-2 text-xs font-bold text-slate-700 uppercase tracking-wide font-montserrat hover:text-[#B1336E] transition-colors mx-auto"
                    >
                      Entregas
                      {sortBy === 'total_deliveries' && (
                        <span className="text-[#B1336E]">
                          {sortOrder === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </button>
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-bold text-slate-700 uppercase tracking-wide font-montserrat hidden lg:table-cell">
                    Estado
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-bold text-slate-700 uppercase tracking-wide font-montserrat">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredDrivers.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-4 py-12 text-center">
                      <div className="flex flex-col items-center justify-center text-slate-400">
                        <Search className="w-12 h-12 mb-3" />
                        <p className="font-montserrat">No se encontraron conductores</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredDrivers.map((driver) => (
                    <tr
                      key={driver.id}
                      className="hover:bg-slate-50 transition-colors cursor-pointer"
                      onClick={() => openDriverModal(driver)}
                    >
                      {/* Conductor */}
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-3">
                          {driver.profile_image ? (
                            <img
                              src={driver.profile_image}
                              alt={driver.name}
                              className="w-10 h-10 rounded-full object-cover border-2 border-slate-200"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-[#B1336E] flex items-center justify-center">
                              <span className="text-white font-bold font-montserrat text-sm">
                                {driver.name?.charAt(0).toUpperCase() || 'C'}
                              </span>
                            </div>
                          )}
                          <div>
                            <p className="font-semibold text-slate-800 font-montserrat">
                              {driver.name || 'Sin nombre'}
                            </p>
                            <p className="text-xs text-slate-500 font-montserrat md:hidden">
                              {driver.phone}
                            </p>
                          </div>
                        </div>
                      </td>

                      {/* Contacto */}
                      <td className="px-4 py-4 hidden md:table-cell">
                        <div>
                          <p className="text-sm text-slate-700 font-montserrat">
                            {driver.phone}
                          </p>
                          <p className="text-xs text-slate-500 font-montserrat">
                            {driver.email}
                          </p>
                        </div>
                      </td>

                      {/* Éxito (%) */}
                      <td className="px-4 py-4">
                        <div className="flex items-center justify-center">
                          <div className={`px-3 py-1.5 rounded-lg ${getSuccessRateBg(driver.success_rate)} flex items-center gap-2`}>
                            <span className={`font-bold font-montserrat ${getSuccessRateColor(driver.success_rate)}`}>
                              {driver.success_rate}%
                            </span>
                            <span className={getSuccessRateColor(driver.success_rate)}>
                              {getPerformanceIcon(driver.success_rate)}
                            </span>
                          </div>
                        </div>
                      </td>

                      {/* Entregas */}
                      <td className="px-4 py-4 text-center">
                        <span className="font-semibold text-slate-700 font-montserrat">
                          {driver.total_deliveries}
                        </span>
                      </td>

                      {/* Estado */}
                      <td className="px-4 py-4 text-center hidden lg:table-cell">
                        {driver.is_active ? (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#67A43C]/10 text-[#67A43C] text-xs font-semibold font-montserrat">
                            <span className="w-2 h-2 rounded-full bg-[#67A43C]"></span>
                            Activo
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-100 text-slate-500 text-xs font-semibold font-montserrat">
                            <span className="w-2 h-2 rounded-full bg-slate-400"></span>
                            Inactivo
                          </span>
                        )}
                      </td>

                      {/* Acciones */}
                      <td className="px-4 py-4 text-right">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openDriverModal(driver);
                          }}
                          className="px-4 py-2 bg-[#B1336E] text-white rounded-lg font-semibold font-montserrat hover:bg-[#B1336E]/90 transition-colors text-sm"
                        >
                          Ver Detalle
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Performance Alert */}
        {drivers.filter(d => d.success_rate < 75).length > 0 && (
          <div className="mt-6 bg-red-50 border border-red-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-bold text-red-800 font-montserrat mb-1">
                  Conductores con Bajo Rendimiento
                </h3>
                <p className="text-sm text-red-700 font-montserrat">
                  {drivers.filter(d => d.success_rate < 75).length} conductor(es) tienen una tasa de éxito menor al 75%.
                  Considera revisar su desempeño y brindar soporte adicional.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Driver Detail Modal */}
      {showModal && selectedDriver && (
        <DriverDetailModal
          driverId={selectedDriver.id}
          onClose={closeModal}
          onDriverUpdated={handleDriverUpdated}
        />
      )}
    </div>
  );
}

export default AdminDrivers;